import { useEffect, useRef, useState } from "react";

import { prefersReducedMotion } from "../lib/motion";
import "./ScrollProgress.css";

/**
 * Back-to-top control with a progress ring — the reference has the same
 * affordance bottom-right. The ring reports how far down the page you are, so
 * it doubles as a position indicator.
 */
const LENGTH = 307.919; // circumference of the r=49 circle in the viewBox

export default function ScrollProgress({ lenisRef }) {
  const pathRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let raf = 0;

    const update = () => {
      raf = 0;
      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      const progress = max > 0 ? Math.min(1, window.scrollY / max) : 0;

      if (pathRef.current) {
        pathRef.current.style.strokeDashoffset = String(LENGTH - LENGTH * progress);
      }
      setVisible(window.scrollY > 380);
    };

    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  const toTop = () => {
    const lenis = lenisRef?.current;
    if (lenis) lenis.scrollTo(0, { duration: 1.3 });
    else window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? "auto" : "smooth" });
  };

  return (
    <button
      type="button"
      className="scroll-progress"
      data-visible={visible}
      onClick={toTop}
      tabIndex={visible ? 0 : -1}
      /* While faded out it is still opacity:0 in the layout, so without this a
         screen reader browsing linearly would announce an invisible button. */
      aria-hidden={!visible}
    >
      <span className="sr-only">Back to top</span>
      <svg className="scroll-progress__ring" viewBox="-1 -1 102 102" aria-hidden="true">
        <path
          className="scroll-progress__track"
          d="M50,1 a49,49 0 0,1 0,98 a49,49 0 0,1 0,-98"
        />
        <path
          ref={pathRef}
          className="scroll-progress__bar"
          d="M50,1 a49,49 0 0,1 0,98 a49,49 0 0,1 0,-98"
          style={{ strokeDasharray: LENGTH, strokeDashoffset: LENGTH }}
        />
      </svg>
      <svg className="scroll-progress__arrow" viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M12 19V5M5 12l7-7 7 7"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
    </button>
  );
}
